// frontend/src/pages/Home.jsx
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faTruck,
  faCow,
  faLeaf,
  faRecycle,
  faGem,
  faTag,
  faShippingFast,
  faHeadset,
  faStarHalfAlt,
  faUsers,
  faGlobeAfrica,
  faClock,
  faCheckCircle,
  faShoppingCart,
  faPlay,
  faPause,
  faVolumeMute,
  faVolumeUp,
  faQuoteLeft,
  faTractor,
  faStore,
  faSeedling,
  faSpinner,
  faMobileAlt,
  faMoneyBillWave,
  faShieldAlt,
  faCertificate,
  faClipboardCheck,
  faHandshake,
  faCalendarCheck,
  faMoneyBillTransfer,
  faCreditCard,
  faStar as faStarSolid,
  faWhatsapp,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faClock as faClockIcon,
  faAward,
  faRocket,
  faThumbsUp,
  faHeart,
  faChevronRight,
  faInfoCircle,
  faPercent,
  faGift,
  faCheckDouble,
  faBoxOpen,
  faWarehouse,
  faShip,
  faLeaf as faLeafIcon,
  faDroplet,
  faSun,
  faCloud,
  faTree,
  faMountain,
  faWater,
  faBird,
  faDog,
  faCat,
  faFish,
  faHorse,
  faDragon,
  faPaw,
  faBone,
  faEgg,
  faBacon,
  faDrumstick,
  faWheatAwn,
  faCorn,
  faApple,
  faCarrot,
  faPepper,
  faCabbage,
  faBroccoli,
  faEggplant,
  faTomato,
  faPotato,
  faOnion,
  faGarlic,
  faLemon,
  faOrange,
  faBanana,
  faAppleWhole,
  faGrape,
  faStrawberry,
  faWatermelon,
  faMelon,
  faPeach,
  faPear,
  faPlum,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import { getProducts, getApprovedTestimonials } from "../api";
import Button from "../components/UI/Button";
import Loader from "../components/UI/Loader";
import CurrencyDisplay from "../components/UI/CurrencyDisplay";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import heroVideo from "../assets/0523.mp4";
import heroVideoPoster from "../assets/0523-poster.jpg";

// ============================================
// CONSTANTS
// ============================================
const STAGGER_DELAY = 0.1;
const FEATURED_PRODUCTS_LIMIT = 6;
const TESTIMONIALS_LIMIT = 4;

const BRAND = {
  primary: "#006B2E",
  primaryRgb: "0, 107, 46",
  secondary: "#D9A321",
  secondaryRgb: "217, 163, 33",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  lightBg: "#F8F8F8",
  white: "#FFFFFF",
  goldHover: "#B8860B",
};

// ============================================
// HELPER FUNCTIONS
// ============================================
const getProductImage = (product) => {
  if (product?.images?.[0]) return product.images[0];
  if (product?.thumbnail) return product.thumbnail;
  if (product?.image) return product.image;
  return null;
};

const renderStars = (rating) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <>
      {[...Array(fullStars)].map((_, i) => (
        <FontAwesomeIcon 
          key={`full-${i}`} 
          icon={faStarSolid} 
          style={{ color: BRAND.secondary }} 
        />
      ))}
      {hasHalfStar && (
        <FontAwesomeIcon 
          key="half" 
          icon={faStarHalfAlt} 
          style={{ color: BRAND.secondary }} 
        />
      )}
      {[...Array(emptyStars)].map((_, i) => (
        <FontAwesomeIcon 
          key={`empty-${i}`} 
          icon={faStarSolid} 
          style={{ color: BRAND.secondary, opacity: 0.3 }} 
        />
      ))}
    </>
  );
};

// ============================================
// SECTION COMPONENTS
// ============================================

// 1. HERO SECTION
const HeroSection = ({
  videoPlaying,
  videoMuted,
  videoRef,
  videoLoaded,
  onVideoLoaded,
  toggleVideoPlay,
  toggleVideoMute,
}) => {
  const { user } = useAuth();

  return (
    <section className="hero-section">
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        poster={heroVideoPoster}
        onLoadedData={() => onVideoLoaded(true)}
        className="hero-video"
      >
        <source src={heroVideo} type="video/mp4" />
      </video>

      <div className="hero-poster" style={{ opacity: videoLoaded ? 0 : 1 }} />
      <div className="hero-overlay" />

      <div className="hero-controls">
        <button onClick={toggleVideoPlay} aria-label={videoPlaying ? "Pause video" : "Play video"}>
          <FontAwesomeIcon icon={videoPlaying ? faPause : faPlay} />
        </button>
        <button onClick={toggleVideoMute} aria-label={videoMuted ? "Unmute video" : "Mute video"}>
          <FontAwesomeIcon icon={videoMuted ? faVolumeMute : faVolumeUp} />
        </button>
      </div>

      <div className="hero-content">
        <motion.div
          className="hero-text"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="hero-badge">
            <FontAwesomeIcon icon={faSeedling} /> Kenali Enterprise
          </span>

          <h1>
            Premium Livestock <br />
            <span className="highlight">Nutrition Solutions</span>
          </h1>

          <p>
            Trusted by farmers across Sierra Leone. Premium animal feed supplements
            sourced from Malaysia, delivered with real-time tracking.
          </p>

          <div className="hero-buttons">
            <Button href="/products" variant="primary" size="large">
              Shop Now <FontAwesomeIcon icon={faArrowRight} />
            </Button>
            <Button href="/preorder" variant="secondary" size="large">
              <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order
            </Button>
            <Button href={user ? "/track-order" : "/login"} variant="outline" size="large">
              {user ? "Track Order" : "Login"}
            </Button>
          </div>

          <div className="hero-stats">
            <div className="hero-stat">
              <FontAwesomeIcon icon={faUsers} />
              <span>500+ Happy Farmers</span>
            </div>
            <div className="hero-stat">
              <FontAwesomeIcon icon={faGlobeAfrica} />
              <span>Malaysia to Sierra Leone</span>
            </div>
            <div className="hero-stat">
              <FontAwesomeIcon icon={faClock} />
              <span>24/7 Support</span>
            </div>
          </div>
        </motion.div>
      </div>

      <style>{`
        .hero-section {
          position: relative;
          min-height: 85vh;
          display: flex;
          align-items: center;
          color: #ffffff;
          overflow: hidden;
        }

        .hero-video {
          position: absolute;
          top: 50%;
          left: 50%;
          min-width: 100%;
          min-height: 100%;
          width: auto;
          height: auto;
          transform: translate(-50%, -50%);
          object-fit: cover;
          opacity: 1;
          transition: opacity 0.5s ease;
        }

        .hero-poster {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: url(${heroVideoPoster});
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          transition: opacity 0.5s ease;
        }

        .hero-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,107,46,0.85) 100%);
        }

        .hero-controls {
          position: absolute;
          bottom: 2rem;
          right: 2rem;
          z-index: 3;
          display: flex;
          gap: 0.75rem;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .hero-controls button {
          background: rgba(0,0,0,0.5);
          border: none;
          border-radius: 50%;
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: white;
          font-size: 1rem;
          backdrop-filter: blur(5px);
          transition: background 0.3s ease;
        }

        .hero-controls button:hover {
          background: rgba(217,163,33,0.7);
        }

        .hero-content {
          position: relative;
          z-index: 2;
          width: 100%;
          padding: 2rem;
        }

        .hero-text {
          max-width: 700px;
        }

        .hero-badge {
          display: inline-block;
          background: rgba(217, 163, 33, 0.2);
          backdrop-filter: blur(10px);
          padding: 0.5rem 1.25rem;
          border-radius: 50px;
          font-size: 0.875rem;
          font-weight: 500;
          letter-spacing: 1px;
          margin-bottom: 1.5rem;
        }

        .hero-text h1 {
          font-size: clamp(2.5rem, 6vw, 4.5rem);
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 1.5rem;
        }

        .hero-text h1 .highlight {
          color: #D9A321;
          display: block;
        }

        .hero-text p {
          font-size: 1.125rem;
          max-width: 600px;
          margin-bottom: 2rem;
          opacity: 0.9;
          line-height: 1.6;
        }

        .hero-buttons {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .hero-stats {
          display: flex;
          gap: 2rem;
          flex-wrap: wrap;
        }

        .hero-stat {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
        }

        .hero-stat svg {
          color: #D9A321;
        }

        .hero-section:hover .hero-controls {
          opacity: 1;
        }

        @media (max-width: 768px) {
          .hero-section {
            min-height: 70vh;
          }
          .hero-controls {
            opacity: 1;
          }
          .hero-content {
            padding: 1.5rem;
          }
          .hero-buttons {
            flex-direction: column;
            align-items: stretch;
          }
          .hero-stats {
            gap: 1rem;
          }
          .hero-stat {
            font-size: 0.75rem;
          }
        }

        @media (max-width: 480px) {
          .hero-section {
            min-height: 60vh;
          }
          .hero-text h1 {
            font-size: 1.75rem;
          }
          .hero-text p {
            font-size: 0.875rem;
          }
          .hero-badge {
            font-size: 0.75rem;
            padding: 0.25rem 0.75rem;
          }
          .hero-controls button {
            width: 36px;
            height: 36px;
            font-size: 0.8rem;
          }
        }
      `}</style>
    </section>
  );
};

// 2. PRE-ORDER PROMO SECTION
const PreOrderPromoSection = () => (
  <section className="preorder-promo">
    <div className="container">
      <motion.div
        className="preorder-content"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="preorder-icon">🌾</div>
        <h2>Pre-Order Kenali GrowPro™</h2>
        <p>
          Reserve your 25kg or 50kg pack before the public release.
          <br />
          <strong className="highlight">50% deposit now, 50% balance on pickup.</strong>
        </p>
        <div className="preorder-buttons">
          <Button href="/preorder" variant="primary" size="large">
            <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order Now
          </Button>
          <a
            href="https://wa.me/23231030808"
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-btn"
          >
            <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp Order
          </a>
        </div>
        <div className="preorder-note">
          Limited stock available • Priority access for pre-order customers
        </div>
      </motion.div>
    </div>

    <style>{`
      .preorder-promo {
        padding: 3rem 0;
        background: linear-gradient(135deg, #063D1C, #0B5E2D);
      }

      .preorder-content {
        text-align: center;
        color: #ffffff;
        max-width: 700px;
        margin: 0 auto;
      }

      .preorder-icon {
        font-size: 2.5rem;
        margin-bottom: 1rem;
      }

      .preorder-content h2 {
        color: #D9A321;
        margin-bottom: 0.5rem;
        font-size: clamp(1.5rem, 3vw, 2rem);
      }

      .preorder-content p {
        opacity: 0.9;
        margin-bottom: 1.5rem;
        font-size: clamp(0.875rem, 1.5vw, 1rem);
      }

      .preorder-content .highlight {
        color: #D9A321;
      }

      .preorder-buttons {
        display: flex;
        gap: 1rem;
        justify-content: center;
        flex-wrap: wrap;
      }

      .whatsapp-btn {
        padding: 0.75rem 2rem;
        background: #25d366;
        color: #ffffff;
        border-radius: 8px;
        text-decoration: none;
        font-weight: 600;
        font-size: 1rem;
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        transition: background 0.3s ease;
      }

      .whatsapp-btn:hover {
        background: #1da851;
      }

      .preorder-note {
        margin-top: 1rem;
        font-size: 0.75rem;
        opacity: 0.7;
      }

      @media (max-width: 480px) {
        .preorder-promo {
          padding: 2rem 0;
        }
        .preorder-buttons {
          flex-direction: column;
          align-items: stretch;
        }
        .whatsapp-btn {
          justify-content: center;
        }
      }
    `}</style>
  </section>
);

// 3. TRUST & COMPLIANCE SECTION
const TrustSection = () => {
  const trustBadges = [
    { icon: faCertificate, label: "NRA Registered", detail: "No: 1001577205", color: BRAND.secondary },
    { icon: faShieldAlt, label: "Quality Guaranteed", detail: "COA Available", color: "#10b981" },
    { icon: faClipboardCheck, label: "Batch Tracked", detail: "Every order traceable", color: "#3b82f6" },
    { icon: faHandshake, label: "Trusted Partner", detail: "Since 2024", color: "#8b5cf6" },
  ];

  return (
    <section className="trust-section">
      <div className="container">
        <div className="trust-grid">
          {trustBadges.map((badge, index) => (
            <motion.div
              key={index}
              className="trust-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * STAGGER_DELAY }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
            >
              <FontAwesomeIcon icon={badge.icon} style={{ color: badge.color, fontSize: "2rem" }} />
              <h4>{badge.label}</h4>
              <p>{badge.detail}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        .trust-section {
          padding: 3rem 0;
          background: #F8F8F8;
        }

        .trust-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1.5rem;
          text-align: center;
        }

        .trust-card {
          padding: 1.5rem;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          transition: all 0.3s ease;
        }

        .trust-card:hover {
          box-shadow: 0 8px 24px rgba(0,0,0,0.1);
        }

        .trust-card h4 {
          font-size: 1rem;
          color: #1F1F1F;
          margin: 0.75rem 0 0.25rem;
        }

        .trust-card p {
          font-size: 0.813rem;
          color: #5A5A5A;
          margin: 0;
        }

        @media (max-width: 480px) {
          .trust-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 0.75rem;
          }
          .trust-card {
            padding: 1rem;
          }
          .trust-card h4 {
            font-size: 0.875rem;
          }
          .trust-card p {
            font-size: 0.7rem;
          }
        }
      `}</style>
    </section>
  );
};

// 4. FEATURES SECTION
const FeaturesSection = () => {
  const features = useMemo(
    () => [
      { icon: faGem, title: "Premium Quality", desc: "Sourced from top manufacturers in Malaysia with full traceability", color: BRAND.secondary },
      { icon: faTag, title: "Best Prices", desc: "Competitive pricing with bulk discounts for farmers", color: "#10b981" },
      { icon: faShippingFast, title: "Fast Delivery", desc: "Real-time tracking from port to farm in Sierra Leone", color: "#3b82f6" },
      { icon: faHeadset, title: "Expert Support", desc: "Technical guidance via WhatsApp for optimal results", color: "#8b5cf6" },
      { icon: faMobileAlt, title: "Mobile Money", desc: "Pay with Orange Money, Afrimoney & WAVE", color: "#f59e0b" },
      { icon: faTractor, title: "Farmer Focused", desc: "Bulk and small-pack options for every farm size", color: BRAND.primary },
      { icon: faCalendarCheck, title: "Pre-Order Available", desc: "Reserve your GrowPro with 50% deposit", color: BRAND.secondary },
      { icon: faGlobeAfrica, title: "Malaysia Origin", desc: "Premium quality products sourced from Malaysia", color: "#8b5cf6" },
    ],
    []
  );

  return (
    <section className="features-section">
      <div className="container">
        <div className="features-header">
          <span className="section-tag">Why Kenali Enterprise</span>
          <h2>Designed for Sierra Leone Farmers</h2>
          <p>We understand the unique challenges of livestock farming in Sierra Leone</p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * STAGGER_DELAY }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="feature-icon" style={{ background: `${feature.color}15` }}>
                <FontAwesomeIcon icon={feature.icon} style={{ color: feature.color, fontSize: "2rem" }} />
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        .features-section {
          padding: 5rem 0;
          background: #ffffff;
        }

        .features-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .section-tag {
          display: inline-block;
          background: rgba(217, 163, 33, 0.1);
          color: #D9A321;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .features-header h2 {
          margin-bottom: 1rem;
          font-size: clamp(1.5rem, 3vw, 2.5rem);
        }

        .features-header p {
          color: #5A5A5A;
          max-width: 600px;
          margin: 0 auto;
          font-size: clamp(0.875rem, 1.5vw, 1rem);
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
        }

        .feature-card {
          text-align: center;
          padding: 2rem;
          border-radius: 16px;
          background: #ffffff;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          border: 1px solid #f0f0f0;
          transition: all 0.3s ease;
        }

        .feature-card:hover {
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          border-color: #D9A321;
        }

        .feature-icon {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }

        .feature-card h3 {
          font-size: 1.25rem;
          margin-bottom: 0.5rem;
          color: #1F1F1F;
        }

        .feature-card p {
          color: #5A5A5A;
          font-size: 0.875rem;
          line-height: 1.5;
        }

        @media (max-width: 480px) {
          .features-section {
            padding: 3rem 0;
          }
          .features-grid {
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
          }
          .feature-card {
            padding: 1.25rem;
          }
          .feature-card h3 {
            font-size: 0.875rem;
          }
          .feature-card p {
            font-size: 0.75rem;
          }
          .feature-icon {
            width: 50px;
            height: 50px;
          }
          .feature-icon svg {
            font-size: 1.25rem !important;
          }
        }

        @media (max-width: 360px) {
          .features-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
};

// 5. PAYMENT METHODS SECTION
const PaymentMethodsSection = () => {
  const paymentMethods = [
    { icon: faMoneyBillWave, label: "Cash on Delivery", color: "#10b981" },
    { icon: faMobileAlt, label: "Orange Money", color: "#f59e0b" },
    { icon: faMobileAlt, label: "Afrimoney", color: "#ef4444" },
    { icon: faMobileAlt, label: "WAVE", color: "#3b82f6" },
    { icon: faMoneyBillTransfer, label: "Bank Transfer", color: "#8b5cf6" },
    { icon: faCreditCard, label: "Card Payment", color: "#6b7280" },
  ];

  return (
    <section className="payments-section">
      <div className="container">
        <div className="payments-header">
          <span className="section-tag">Flexible Payments</span>
          <h3>Multiple Payment Options for Sierra Leone</h3>
          <p>Pay the way that works best for you</p>
        </div>

        <div className="payments-grid">
          {paymentMethods.map((method, index) => (
            <motion.div
              key={index}
              className="payment-method"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              viewport={{ once: true }}
            >
              <FontAwesomeIcon icon={method.icon} style={{ color: method.color }} />
              <span>{method.label}</span>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        .payments-section {
          padding: 3rem 0;
          background: #ffffff;
        }

        .payments-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .payments-header .section-tag {
          display: inline-block;
          background: rgba(217, 163, 33, 0.1);
          color: #D9A321;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .payments-header h3 {
          font-size: 1.25rem;
          color: #006B2E;
          margin-bottom: 0.25rem;
        }

        .payments-header p {
          color: #5A5A5A;
          font-size: 0.875rem;
        }

        .payments-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 1rem;
        }

        .payment-method {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1.25rem;
          background: #F8F8F8;
          border-radius: 25px;
          border: 1px solid #e5e5e5;
          font-size: 0.875rem;
          color: #1F1F1F;
          transition: all 0.3s ease;
        }

        .payment-method:hover {
          border-color: #D9A321;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }

        @media (max-width: 480px) {
          .payments-grid {
            gap: 0.5rem;
          }
          .payment-method {
            padding: 0.35rem 0.75rem;
            font-size: 0.75rem;
          }
          .payment-method svg {
            font-size: 0.8rem;
          }
        }
      `}</style>
    </section>
  );
};

// 6. CATEGORIES SECTION
const CategoriesSection = () => {
  const categories = useMemo(
    () => [
      { name: "Rumen Bypass Fat", icon: faCow, slug: "rumen-bypass-fat", color: BRAND.primary, description: "Boost milk production & energy" },
      { name: "Mineral Blocks", icon: faGem, slug: "mineral-blocks", color: BRAND.secondary, description: "Essential minerals & salts" },
      { name: "Protein Concentrates", icon: faLeaf, slug: "protein-concentrates", color: "#16a34a", description: "High protein for growth" },
      { name: "Feed Supplements", icon: faRecycle, slug: "feed-supplements", color: "#8b5cf6", description: "Complete nutrition mix" },
    ],
    []
  );

  return (
    <section className="categories-section">
      <div className="container">
        <div className="categories-header">
          <span className="section-tag">Shop by Category</span>
          <h2>Find the Right Product for Your Livestock</h2>
          <p>Browse our range of premium animal feed supplements</p>
        </div>

        <div className="categories-grid">
          {categories.map((category, index) => (
            <motion.div
              key={index}
              className="category-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * STAGGER_DELAY }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <Link to={`/products?category=${category.slug}`} className="category-link">
                <div className="category-icon" style={{ background: `${category.color}15` }}>
                  <FontAwesomeIcon icon={category.icon} style={{ color: category.color, fontSize: "2rem" }} />
                </div>
                <h3>{category.name}</h3>
                <p>{category.description}</p>
                <span className="shop-link">Shop Now →</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        .categories-section {
          padding: 5rem 0;
          background: #F8F8F8;
        }

        .categories-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .categories-header .section-tag {
          display: inline-block;
          background: rgba(217, 163, 33, 0.1);
          color: #D9A321;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .categories-header h2 {
          margin-bottom: 1rem;
          font-size: clamp(1.5rem, 3vw, 2.5rem);
        }

        .categories-header p {
          color: #5A5A5A;
          max-width: 600px;
          margin: 0 auto;
          font-size: clamp(0.875rem, 1.5vw, 1rem);
        }

        .categories-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 2rem;
        }

        .category-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .category-card:hover {
          box-shadow: 0 8px 24px rgba(0,0,0,0.1);
          border-color: #D9A321;
        }

        .category-link {
          text-decoration: none;
          display: block;
        }

        .category-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }

        .category-card h3 {
          font-size: 1.125rem;
          margin-bottom: 0.5rem;
          color: #1F1F1F;
        }

        .category-card p {
          color: #5A5A5A;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }

        .shop-link {
          color: #D9A321;
          font-weight: 600;
          font-size: 0.875rem;
        }

        @media (max-width: 480px) {
          .categories-section {
            padding: 3rem 0;
          }
          .categories-grid {
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
          }
          .category-card {
            padding: 1.25rem;
          }
          .category-card h3 {
            font-size: 0.875rem;
          }
          .category-card p {
            font-size: 0.7rem;
          }
          .category-icon {
            width: 60px;
            height: 60px;
          }
          .category-icon svg {
            font-size: 1.5rem !important;
          }
          .shop-link {
            font-size: 0.75rem;
          }
        }

        @media (max-width: 360px) {
          .categories-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
};

// 7. FEATURED PRODUCTS SECTION
const FeaturedProductsSection = ({ products, loading, addToCartHandler, addingToCart }) => {
  return (
    <section className="featured-section">
      <div className="container">
        <div className="featured-header">
          <span className="section-tag">Best Sellers</span>
          <h2>Featured Products</h2>
          <p>Our most popular products trusted by farmers across Sierra Leone</p>
        </div>

        {loading ? (
          <div className="loader-container">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: BRAND.primary }} />
          </div>
        ) : (
          <>
            <div className="products-grid">
              {products.length > 0 ? (
                products.slice(0, FEATURED_PRODUCTS_LIMIT).map((product, index) => {
                  const imageUrl = getProductImage(product);
                  const price = product.basePrice?.usd || product.pricing?.usd || 0;
                  const isInStock = product.stock > 0;

                  return (
                    <motion.div
                      key={product._id}
                      className="product-card"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * STAGGER_DELAY }}
                      viewport={{ once: true }}
                      whileHover={{ y: -5 }}
                    >
                      <div className="product-image">
                        {imageUrl ? (
                          <img src={imageUrl} alt={product.name} loading="lazy" />
                        ) : (
                          <div className="product-image-placeholder">
                            <FontAwesomeIcon icon={faCow} style={{ fontSize: "3rem", color: BRAND.slate }} />
                          </div>
                        )}
                        {product.featured && <span className="badge best-seller">Best Seller</span>}
                        {product.slug === "kenali-growpro" && (
                          <span className="badge preorder-badge">
                            <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order
                          </span>
                        )}
                        {!isInStock && <span className="badge out-of-stock">Out of Stock</span>}
                        <div
                          className="whatsapp-quick"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            window.open("https://wa.me/23231030808", "_blank");
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <FontAwesomeIcon icon={faWhatsappBrand} />
                          WhatsApp
                        </div>
                      </div>

                      <div className="product-body">
                        <Link to={`/products/${product.slug}`} className="product-link">
                          <h3>{product.name}</h3>
                          <div className="product-category">{product.category?.replace(/-/g, " ")}</div>
                        </Link>
                        <div className="product-price">
                          <CurrencyDisplay amountUSD={price} showOriginal={false} />
                          <small>/{product.unit || "kg"}</small>
                        </div>
                        {product.stockStatus === "low_stock" && (
                          <div className="low-stock-warning">⚡ Low Stock - Only {product.stock} left</div>
                        )}
                        <Button
                          onClick={(e) => addToCartHandler(product._id, e)}
                          variant="primary"
                          size="small"
                          disabled={addingToCart[product._id] || !isInStock}
                          className="add-to-cart-btn"
                        >
                          {addingToCart[product._id] ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin /> Adding...
                            </>
                          ) : !isInStock ? (
                            "Out of Stock"
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faShoppingCart} /> Add to Cart
                            </>
                          )}
                        </Button>
                        {product.slug === "kenali-growpro" && (
                          <div className="preorder-link">
                            <Link to="/preorder">
                              <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order Available
                            </Link>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })
              ) : (
                <div className="empty-products">
                  <p>No featured products available at the moment.</p>
                  <Button href="/products" variant="primary">View All Products</Button>
                </div>
              )}
            </div>
            {products.length > 0 && (
              <div className="view-all">
                <Button href="/products" variant="primary">
                  View All Products <FontAwesomeIcon icon={faArrowRight} />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        .featured-section {
          padding: 5rem 0;
          background: #ffffff;
        }

        .featured-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .featured-header .section-tag {
          display: inline-block;
          background: rgba(217, 163, 33, 0.1);
          color: #D9A321;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .featured-header h2 {
          margin-bottom: 1rem;
          font-size: clamp(1.5rem, 3vw, 2.5rem);
        }

        .featured-header p {
          color: #5A5A5A;
          max-width: 600px;
          margin: 0 auto;
          font-size: clamp(0.875rem, 1.5vw, 1rem);
        }

        .loader-container {
          display: flex;
          justify-content: center;
          padding: 4rem;
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }

        .product-card {
          background: #ffffff;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
          border: 1px solid #f0f0f0;
          display: flex;
          flex-direction: column;
        }

        .product-card:hover {
          box-shadow: 0 12px 40px rgba(0,0,0,0.12);
          border-color: #D9A321;
        }

        .product-image {
          position: relative;
          height: 220px;
          overflow: hidden;
          background: #F8F8F8;
        }

        .product-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .product-card:hover .product-image img {
          transform: scale(1.05);
        }

        .product-image-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .badge {
          position: absolute;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.7rem;
          font-weight: 600;
          color: #ffffff;
        }

        .best-seller {
          top: 1rem;
          right: 1rem;
          background: #D9A321;
        }

        .preorder-badge {
          top: 1rem;
          left: 1rem;
          background: #D9A321;
        }

        .out-of-stock {
          top: 1rem;
          left: 1rem;
          background: #dc2626;
        }

        .whatsapp-quick {
          position: absolute;
          bottom: 1rem;
          left: 1rem;
          background: #25d366;
          color: #ffffff;
          padding: 0.3rem 0.75rem;
          border-radius: 20px;
          font-size: 0.7rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          cursor: pointer;
          z-index: 5;
          border: none;
          transition: background 0.3s ease;
        }

        .whatsapp-quick:hover {
          background: #1da851;
        }

        .product-body {
          padding: 1.25rem;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .product-link {
          text-decoration: none;
        }

        .product-link h3 {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
          color: #1F1F1F;
          min-height: 50px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .product-category {
          font-size: 0.75rem;
          color: #5A5A5A;
          margin-bottom: 0.5rem;
          text-transform: capitalize;
        }

        .product-price {
          font-size: 1.25rem;
          font-weight: 700;
          color: #D9A321;
          margin-bottom: 0.5rem;
        }

        .product-price small {
          font-size: 0.75rem;
          color: #5A5A5A;
          font-weight: 400;
        }

        .low-stock-warning {
          font-size: 0.7rem;
          color: #f59e0b;
          margin-bottom: 0.5rem;
        }

        .add-to-cart-btn {
          width: 100%;
        }

        .preorder-link {
          margin-top: 0.5rem;
          text-align: center;
        }

        .preorder-link a {
          font-size: 0.75rem;
          color: #D9A321;
          font-weight: 600;
          text-decoration: none;
        }

        .empty-products {
          text-align: center;
          padding: 4rem;
          color: #5A5A5A;
          grid-column: 1/-1;
        }

        .view-all {
          text-align: center;
          margin-top: 2rem;
        }

        @media (max-width: 480px) {
          .featured-section {
            padding: 3rem 0;
          }
          .products-grid {
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
          }
          .product-image {
            height: 150px;
          }
          .product-body {
            padding: 0.75rem;
          }
          .product-link h3 {
            font-size: 0.8rem;
            min-height: 35px;
          }
          .product-price {
            font-size: 0.9rem;
          }
          .badge {
            font-size: 0.55rem;
            padding: 0.15rem 0.5rem;
          }
          .whatsapp-quick {
            font-size: 0.55rem;
            padding: 0.2rem 0.5rem;
          }
        }

        @media (max-width: 360px) {
          .products-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
};

// 8. TESTIMONIALS SECTION
const TestimonialsSection = ({ testimonials, stats }) => {
  return (
    <section className="testimonials-section">
      <div className="container">
        <div className="testimonials-header">
          <span className="section-tag">Testimonials</span>
          <h2>What Our Customers Say</h2>
          <p>
            {stats.total || testimonials.length}+ satisfied farmers and customers
            {stats.averageRating && ` • ${stats.averageRating.toFixed(1)} / 5 average rating`}
          </p>
        </div>

        <div className="testimonials-grid">
          {testimonials.length > 0 ? (
            testimonials.slice(0, TESTIMONIALS_LIMIT).map((testimonial, index) => (
              <motion.div
                key={testimonial._id}
                className="testimonial-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * STAGGER_DELAY }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="testimonial-quote">
                  <FontAwesomeIcon icon={faQuoteLeft} />
                </div>
                <div className="testimonial-stars">{renderStars(testimonial.rating)}</div>
                <h3>{testimonial.title}</h3>
                <p>
                  "{testimonial.content.length > 200 ? testimonial.content.substring(0, 200) + "..." : testimonial.content}"
                </p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">
                    {testimonial.avatar ? (
                      <img src={testimonial.avatar} alt={testimonial.userName} />
                    ) : (
                      <FontAwesomeIcon icon={testimonial.userType === "farmer" ? faTractor : faStore} />
                    )}
                  </div>
                  <div>
                    <strong>{testimonial.userName}</strong>
                    <span>{testimonial.userType === "farmer" ? "🌾 Farmer" : "🛒 Customer"}</span>
                  </div>
                </div>
                {testimonial.productName && (
                  <div className="testimonial-product">Product: {testimonial.productName}</div>
                )}
              </motion.div>
            ))
          ) : (
            <div className="empty-testimonials">
              <p>No testimonials available yet. Be the first to share your experience!</p>
              <Button href="/testimonials" variant="outline">Share Your Experience</Button>
            </div>
          )}
        </div>

        {testimonials.length > 0 && (
          <div className="view-all-testimonials">
            <Button href="/testimonials" variant="outline">Read More Testimonials</Button>
          </div>
        )}
      </div>

      <style>{`
        .testimonials-section {
          padding: 5rem 0;
          background: #F8F8F8;
        }

        .testimonials-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .testimonials-header .section-tag {
          display: inline-block;
          background: rgba(217, 163, 33, 0.1);
          color: #D9A321;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .testimonials-header h2 {
          margin-bottom: 1rem;
          font-size: clamp(1.5rem, 3vw, 2.5rem);
        }

        .testimonials-header p {
          color: #5A5A5A;
          max-width: 600px;
          margin: 0 auto;
          font-size: clamp(0.875rem, 1.5vw, 1rem);
        }

        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .testimonial-card {
          background: #ffffff;
          padding: 2rem;
          border-radius: 16px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
          border: 1px solid #f0f0f0;
          display: flex;
          flex-direction: column;
        }

        .testimonial-card:hover {
          box-shadow: 0 8px 24px rgba(0,0,0,0.1);
          border-color: #D9A321;
        }

        .testimonial-quote {
          margin-bottom: 1rem;
        }

        .testimonial-quote svg {
          font-size: 2rem;
          color: #D9A321;
          opacity: 0.3;
        }

        .testimonial-stars {
          display: flex;
          gap: 0.25rem;
          margin-bottom: 1rem;
        }

        .testimonial-card h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: #006B2E;
        }

        .testimonial-card p {
          color: #5A5A5A;
          line-height: 1.6;
          margin-bottom: 1.5rem;
          font-style: italic;
          flex: 1;
        }

        .testimonial-author {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-top: auto;
        }

        .testimonial-avatar {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #006B2E, #014421);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          overflow: hidden;
          flex-shrink: 0;
        }

        .testimonial-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .testimonial-author strong {
          display: block;
          color: #1F1F1F;
        }

        .testimonial-author span {
          font-size: 0.75rem;
          color: #5A5A5A;
        }

        .testimonial-product {
          margin-top: 0.75rem;
          font-size: 0.7rem;
          color: #D9A321;
        }

        .empty-testimonials {
          text-align: center;
          padding: 4rem;
          color: #5A5A5A;
          grid-column: 1/-1;
        }

        .view-all-testimonials {
          text-align: center;
          margin-top: 2rem;
        }

        @media (max-width: 480px) {
          .testimonials-section {
            padding: 3rem 0;
          }
          .testimonials-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          .testimonial-card {
            padding: 1.25rem;
          }
        }
      `}</style>
    </section>
  );
};

// 9. ABOUT SECTION
const AboutSection = () => {
  const features = [
    "NRA Registered: 1001577205",
    "Quality tested with COA available",
    "Bulk & small-pack options",
    "Real-time shipment tracking",
    "WhatsApp support for farmers",
    "Mobile money payments accepted",
    "Pre-orders available with 50% deposit",
    "Sourced from Malaysia",
  ];

  return (
    <section className="about-section">
      <div className="container">
        <div className="about-grid">
          <motion.div 
            className="about-text" 
            initial={{ opacity: 0, x: -50 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }}
          >
            <span className="section-tag">About Kenali Enterprise</span>
            <h2>Trusted Partner for Farmers Across Sierra Leone</h2>
            <p>
              Kenali Enterprise is dedicated to providing premium animal feed supplements to farmers across Sierra Leone.
              We source our products from trusted manufacturers in Malaysia, ensuring the highest quality at competitive prices.
            </p>
            <div className="about-features">
              {features.map((feature, i) => (
                <div key={i} className="about-feature">
                  <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#16a34a" }} />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
            <div className="about-buttons">
              <Button href="/about" variant="primary">Learn More</Button>
              <a href="https://wa.me/23231030808" target="_blank" rel="noopener noreferrer" className="whatsapp-btn">
                <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp Us
              </a>
            </div>
          </motion.div>

          <motion.div 
            className="about-image" 
            initial={{ opacity: 0, x: 50 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }}
          >
            <div className="about-image-content">
              <FontAwesomeIcon icon={faTruck} className="about-icon" />
              <div className="about-brand">Kenali Enterprise</div>
              <div className="about-tagline">Premium Livestock Nutrition</div>
              <div className="about-nra">NRA: 1001577205</div>
            </div>
          </motion.div>
        </div>
      </div>

      <style>{`
        .about-section {
          padding: 5rem 0;
          background: #ffffff;
        }

        .about-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
        }

        .about-text .section-tag {
          display: inline-block;
          background: rgba(217, 163, 33, 0.1);
          color: #D9A321;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .about-text h2 {
          margin-bottom: 1rem;
          font-size: clamp(1.5rem, 2.5vw, 2.5rem);
        }

        .about-text > p {
          color: #5A5A5A;
          line-height: 1.6;
          margin-bottom: 1.5rem;
          font-size: clamp(0.875rem, 1.2vw, 1rem);
        }

        .about-features {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
          margin-bottom: 2rem;
        }

        .about-feature {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.813rem;
          color: #5A5A5A;
        }

        .about-buttons {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .about-buttons .whatsapp-btn {
          padding: 0.625rem 1.5rem;
          background: #25d366;
          color: #ffffff;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.875rem;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          transition: background 0.3s ease;
        }

        .about-buttons .whatsapp-btn:hover {
          background: #1da851;
        }

        .about-image-content {
          background: linear-gradient(135deg, #006B2E, #014421);
          border-radius: 24px;
          height: 400px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          color: white;
          text-align: center;
        }

        .about-image-content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at center, rgba(217,163,33,0.2) 0%, transparent 70%);
        }

        .about-icon {
          font-size: 5rem;
          opacity: 0.5;
          margin-bottom: 1rem;
          z-index: 1;
        }

        .about-brand {
          font-size: 2rem;
          font-weight: 700;
          z-index: 1;
        }

        .about-tagline {
          font-size: 1rem;
          opacity: 0.8;
          z-index: 1;
        }

        .about-nra {
          margin-top: 1rem;
          font-size: 0.875rem;
          opacity: 0.7;
          z-index: 1;
        }

        @media (max-width: 768px) {
          .about-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }
          .about-features {
            grid-template-columns: 1fr 1fr;
          }
          .about-image-content {
            height: 300px;
          }
        }

        @media (max-width: 480px) {
          .about-section {
            padding: 3rem 0;
          }
          .about-features {
            grid-template-columns: 1fr;
          }
          .about-buttons {
            flex-direction: column;
          }
          .about-image-content {
            height: 220px;
          }
          .about-brand {
            font-size: 1.5rem;
          }
          .about-icon {
            font-size: 3rem;
          }
        }
      `}</style>
    </section>
  );
};

// 10. STATS SECTION
const StatsSection = ({ testimonialStats }) => {
  const stats = useMemo(
    () => [
      { value: testimonialStats.total || "500+", label: "Happy Farmers", icon: faUsers },
      { value: "50+", label: "Tons Delivered", icon: faTruck },
      { value: testimonialStats.averageRating ? `${testimonialStats.averageRating.toFixed(1)} / 5` : "4.8 / 5", label: "Customer Rating", icon: faStarSolid },
      { value: "24/7", label: "WhatsApp Support", icon: faWhatsappBrand },
      { value: "Malaysia", label: "Product Origin", icon: faGlobeAfrica },
    ],
    [testimonialStats]
  );

  return (
    <section className="stats-section">
      <div className="container">
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="stat-item"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * STAGGER_DELAY }}
              viewport={{ once: true }}
            >
              <FontAwesomeIcon icon={stat.icon} />
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        .stats-section {
          padding: 5rem 0;
          background: #F8F8F8;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 2rem;
          text-align: center;
        }

        .stat-item svg {
          font-size: 2.5rem;
          color: #D9A321;
          margin-bottom: 1rem;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 800;
          color: #006B2E;
          margin-bottom: 0.5rem;
        }

        .stat-label {
          color: #5A5A5A;
          font-size: 0.875rem;
        }

        @media (max-width: 480px) {
          .stats-section {
            padding: 3rem 0;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 1.5rem;
          }
          .stat-item svg {
            font-size: 1.75rem;
          }
          .stat-value {
            font-size: 1.25rem;
          }
          .stat-label {
            font-size: 0.75rem;
          }
        }
      `}</style>
    </section>
  );
};

// 11. CTA SECTION
const CTASection = () => {
  return (
    <section className="cta-section">
      <div className="container">
        <div className="cta-content">
          <h2>Ready to Improve Your Livestock Nutrition?</h2>
          <p>Contact us for bulk orders or expert advice on animal nutrition.</p>
          <div className="cta-buttons">
            <Button href="/products" variant="primary" size="large">
              Shop Now <FontAwesomeIcon icon={faArrowRight} />
            </Button>
            <Button href="/contact" variant="outline" size="large">
              Contact Sales
            </Button>
            <Button href="/preorder" variant="secondary" size="large">
              <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order
            </Button>
            <a href="https://wa.me/23231030808" target="_blank" rel="noopener noreferrer" className="whatsapp-btn">
              <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .cta-section {
          background: linear-gradient(135deg, #006B2E, #014421);
          padding: 5rem 2rem;
          text-align: center;
        }

        .cta-content {
          max-width: 700px;
          margin: 0 auto;
        }

        .cta-content h2 {
          color: #ffffff;
          margin-bottom: 1rem;
          font-size: clamp(1.5rem, 3vw, 2.5rem);
        }

        .cta-content p {
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 2rem;
          font-size: clamp(0.875rem, 1.2vw, 1.125rem);
        }

        .cta-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-buttons .whatsapp-btn {
          padding: 0.75rem 2rem;
          background: #25d366;
          color: #ffffff;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          font-size: 1rem;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          transition: background 0.3s ease;
        }

        .cta-buttons .whatsapp-btn:hover {
          background: #1da851;
        }

        @media (max-width: 480px) {
          .cta-section {
            padding: 3rem 1.5rem;
          }
          .cta-buttons {
            flex-direction: column;
            align-items: stretch;
          }
          .cta-buttons .whatsapp-btn {
            justify-content: center;
          }
        }
      `}</style>
    </section>
  );
};

// ============================================
// MAIN HOME COMPONENT
// ============================================
const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialStats, setTestimonialStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState({});
  const [videoPlaying, setVideoPlaying] = useState(true);
  const [videoMuted, setVideoMuted] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);
  const { addItem } = useCart();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [productsRes, testimonialsRes] = await Promise.all([
        getProducts({ featured: true, limit: FEATURED_PRODUCTS_LIMIT }),
        getApprovedTestimonials({ limit: TESTIMONIALS_LIMIT, featured: true }),
      ]);

      setFeaturedProducts(productsRes.data?.products || []);
      setTestimonials(testimonialsRes.data?.testimonials || []);
      setTestimonialStats(testimonialsRes.data?.stats || {});
    } catch (error) {
      console.error("Error fetching home data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddToCart = useCallback(
    async (productId, e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      setAddingToCart((prev) => ({ ...prev, [productId]: true }));
      try {
        await addItem(productId, 1);
      } catch (error) {
        console.error("Error adding to cart:", error);
      } finally {
        setTimeout(() => {
          setAddingToCart((prev) => ({ ...prev, [productId]: false }));
        }, 1000);
      }
    },
    [addItem]
  );

  const handleVideoLoaded = useCallback((loaded) => {
    setVideoLoaded(loaded);
  }, []);

  const toggleVideoPlay = useCallback(() => {
    if (videoRef.current) {
      if (videoPlaying) {
        videoRef.current.pause();
        setVideoPlaying(false);
      } else {
        videoRef.current.play();
        setVideoPlaying(true);
      }
    }
  }, [videoPlaying]);

  const toggleVideoMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !videoMuted;
      setVideoMuted(!videoMuted);
    }
  }, [videoMuted]);

  if (loading) return <Loader />;

  return (
    <div className="home-page">
      <HeroSection
        videoPlaying={videoPlaying}
        videoMuted={videoMuted}
        videoRef={videoRef}
        videoLoaded={videoLoaded}
        onVideoLoaded={handleVideoLoaded}
        toggleVideoPlay={toggleVideoPlay}
        toggleVideoMute={toggleVideoMute}
      />
      <PreOrderPromoSection />
      <TrustSection />
      <FeaturesSection />
      <PaymentMethodsSection />
      <CategoriesSection />
      <FeaturedProductsSection
        products={featuredProducts}
        loading={loading}
        addToCartHandler={handleAddToCart}
        addingToCart={addingToCart}
      />
      <TestimonialsSection testimonials={testimonials} stats={testimonialStats} />
      <AboutSection />
      <StatsSection testimonialStats={testimonialStats} />
      <CTASection />
    </div>
  );
};

export default Home;